import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

import { CalendarEvent, CalendarEventCreate, CalendarEventUpdate, CalendarEventType, CalendarVisibleUser } from '../../../interfaces/calendar-event.interface';
import { CalendarService, AgreedCompany } from '../../../services/calendar.service';

export interface CalendarEventDialogData {
  mode: 'create' | 'edit';
  startDate?: Date;
  endDate?: Date;
  allDay?: boolean;
  event?: CalendarEvent;
  visibleUsers?: CalendarVisibleUser[];
  currentUserId?: number;
  slotDuration?: string;
}

@Component({
  selector: 'app-calendar-event-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatCheckboxModule,
    MatAutocompleteModule
],
  templateUrl: './calendar-event-dialog.component.html',
  styleUrls: ['./calendar-event-dialog.component.scss'],
})
export class CalendarEventDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  title = '';
  description = '';
  startDate: Date | null = null;
  startTime = '09:00';
  endDate: Date | null = null;
  endTime = '10:00';
  allDay = false;
  eventType: CalendarEventType = 'appointment';
  location = '';
  timeSlots: string[] = [];

  isEdit = false;

  // Autocomplete
  agreedCompanies: AgreedCompany[] = [];
  selectedCompanyId: number | null = null;

  // Cél user naptár
  visibleUsers: CalendarVisibleUser[] = [];
  targetUserId: number = -1;

  /** Időpont esetén csak azok a felhasználók jelennek meg, akiknek van KÜM (kam) role-juk */
  get kumVisibleUsers(): CalendarVisibleUser[] {
    return this.visibleUsers.filter(u => u.is_kum === true);
  }

  eventTypes: { value: CalendarEventType; label: string }[] = [
    { value: 'appointment', label: 'Időpont' },
    { value: 'other', label: 'Egyéb' },
  ];

  constructor(
    private dialogRef: MatDialogRef<CalendarEventDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CalendarEventDialogData,
    private cdr: ChangeDetectorRef,
    private calendarService: CalendarService,
  ) {}

  ngOnInit(): void {
    this.timeSlots = this.generateTimeSlots();
    this.isEdit = this.data.mode === 'edit';

    // Látható userek beállítása
    this.visibleUsers = this.data.visibleUsers || [];

    if (this.isEdit && this.data.event) {
      this.populateFromEvent(this.data.event);
    } else {
      this.populateFromSelection();
    }

    // Autocomplete debounce keresés
    this.searchSubject$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) =>
          this.calendarService.getAgreedCompanies(term, term ? 20 : 5)
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res) => {
          this.agreedCompanies = res.companies || [];
          this.cdr.markForCheck();
        },
      });

    // Alapértelmezett 5 legutóbbi betöltése
    if (this.eventType === 'appointment') {
      this.loadDefaultCompanies();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDefaultCompanies(): void {
    this.calendarService.getAgreedCompanies('', 5)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.agreedCompanies = res.companies || [];
          this.cdr.markForCheck();
        },
      });
  }

  onTitleInput(value: string | AgreedCompany): void {
    if (typeof value !== 'string') return;
    this.searchSubject$.next(value);
    // Ha kézzel törlődött a mező, töröljük a kiválasztást és a leírást
    if (!value?.trim()) {
      this.selectedCompanyId = null;
      this.description = '';
      this.cdr.markForCheck();
    }
  }

  onCompanySelected(company: AgreedCompany): void {
    this.title = company.display;
    this.selectedCompanyId = company.id;
    this.loadCompanyDetails(company.id);
  }

  private loadCompanyDetails(companyId: number): void {
    this.calendarService.getCompanyDetails(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.description = this.buildCompanyDescription(res);
          this.cdr.markForCheck();
        },
      });
  }

  private buildCompanyDescription(res: any): string {
    const c = res.company;
    const lines: string[] = [];

    if (c.taxnumber) {
      lines.push(`🔢 Adószám: ${c.taxnumber}`);
    }
    if (c.companyregistrationnumber) {
      lines.push(`📋 Cégjegyzékszám: ${c.companyregistrationnumber}`);
    }
    if (c.address) {
      lines.push(`📍 Cím: ${c.address}`);
    }
    if (c.county) {
      lines.push(`🗺️ Megye: ${c.county}`);
    }
    if (c.mainactivity) {
      lines.push(`💼 Főtevékenység: ${c.mainactivity}`);
    }
    if (c.netrevenue) {
      lines.push(`💰 Nettó árbevétel: ${c.netrevenue.toLocaleString('hu-HU')} Ft`);
    }
    if (c.employeecount) {
      lines.push(`👥 Alkalmazottak: ${c.employeecount} fő`);
    }

    // Cég közvetlen elérhetőségei
    if (c.phones?.length) {
      lines.push(`📞 Cég tel.: ${c.phones.join(', ')}`);
    }
    if (c.emails?.length) {
      lines.push(`📧 Cég email: ${c.emails.join(', ')}`);
    }

    // Céges megjegyzés (utolsó hívás)
    if (c.last_call_notes) {
      lines.push('');
      lines.push(`📝 Megjegyzés: ${c.last_call_notes}`);
    }

    // Tisztségviselők
    const officials = res.officials || [];
    if (officials.length > 0) {
      lines.push('');
      lines.push('👤 Tisztségviselők:');
      for (const off of officials) {
        lines.push(`  • ${off.fullname}`);
        if (off.phones) {
          lines.push(`    📞 ${off.phones}`);
        }
        if (off.emails) {
          lines.push(`    📧 ${off.emails}`);
        }
      }
    }

    return lines.join('\n');
  }

  private generateTimeSlots(): string[] {
    const slots: string[] = [];
    const duration = this.data.slotDuration || '00:15:00';
    const parts = duration.split(':');
    const intervalMinutes = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    for (let h = 6; h <= 20; h++) {
      for (let m = 0; m < 60; m += intervalMinutes) {
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return slots;
  }

  private populateFromEvent(ev: CalendarEvent): void {
    this.title = ev.title;
    this.description = ev.description || '';
    this.location = ev.location || '';
    this.allDay = ev.allDay;
    this.eventType = (ev.event_type as CalendarEventType) || 'appointment';

    // Ha az esemény más user naptárában van, beállítjuk a targetUserId-t
    const currentUserId = this.getCurrentUserId();
    if (ev.user_id && ev.user_id !== currentUserId) {
      this.targetUserId = ev.user_id;
    }

    if (ev.start) {
      const start = new Date(ev.start);
      this.startDate = start;
      this.startTime = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`;
    }
    if (ev.end) {
      const end = new Date(ev.end);
      this.endDate = end;
      this.endTime = `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`;
    }
  }

  private getCurrentUserId(): number {
    return this.data.currentUserId || 0;
  }

  private populateFromSelection(): void {
    if (this.data.startDate) {
      this.startDate = new Date(this.data.startDate);
      this.startTime = `${this.startDate.getHours().toString().padStart(2, '0')}:${this.startDate.getMinutes().toString().padStart(2, '0')}`;
    }
    if (this.data.endDate) {
      this.endDate = new Date(this.data.endDate);
      this.endTime = `${this.endDate.getHours().toString().padStart(2, '0')}:${this.endDate.getMinutes().toString().padStart(2, '0')}`;
    }
    this.allDay = this.data.allDay || false;

    // Ha a start 00:00, alapértelmezett 09:00-10:00
    if (this.startTime === '00:00') {
      this.startTime = '09:00';
      this.endTime = '10:00';
    }
  }

  private formatLocalISO(date: Date, time: string): string {
    const [h, m] = time.split(':').map(Number);
    const d = new Date(date);
    d.setHours(h, m, 0, 0);
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}T${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:00`;
  }

  private buildDateTime(date: Date | null, time: string): Date | null {
    if (!date) return null;
    const [h, m] = time.split(':').map(Number);
    const d = new Date(date);
    d.setHours(h, m, 0, 0);
    return d;
  }

  private getDurationMs(): number {
    const start = this.buildDateTime(this.startDate, this.startTime);
    const end = this.buildDateTime(this.endDate, this.endTime);
    if (!start || !end) return 60 * 60 * 1000; // alapértelmezett 1 óra
    const diff = end.getTime() - start.getTime();
    return diff > 0 ? diff : 60 * 60 * 1000;
  }

  private applyDurationToEnd(durationMs: number): void {
    const newStart = this.buildDateTime(this.startDate, this.startTime);
    if (!newStart) return;
    const newEnd = new Date(newStart.getTime() + durationMs);
    this.endDate = newEnd;
    this.endTime = `${newEnd.getHours().toString().padStart(2, '0')}:${newEnd.getMinutes().toString().padStart(2, '0')}`;
  }

  onStartDateChange(newDate: Date): void {
    // Érvénytelen dátum esetén (pl. kézi gépelés) ne frissítsük az értéket
    if (newDate && isNaN(newDate.getTime())) {
      this.startDate = null;
      return;
    }
    const duration = this.getDurationMs();
    this.startDate = newDate;
    this.applyDurationToEnd(duration);
  }

  onStartTimeChange(newTime: string): void {
    const duration = this.getDurationMs();
    this.startTime = newTime;
    this.applyDurationToEnd(duration);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSave(): void {
    if (!this.title.trim() || !this.isValidDate(this.startDate)) return;
    if (!this.allDay && !this.isValidTime(this.startTime)) return;
    if (!this.allDay && this.endDate && !this.isValidTime(this.endTime)) return;


    const startISO = this.formatLocalISO(this.startDate!, this.startTime);
    const endISO = this.endDate ? this.formatLocalISO(this.endDate, this.endTime) : null;

    // Egész napos eseménynél az időpontot nullázzuk – csak a dátum kerül mentésre
    const effectiveStartTime = this.allDay ? '00:00' : this.startTime;
    const effectiveEndTime = this.allDay ? '00:00' : this.endTime;

   
    if (this.isEdit) {
      const update: CalendarEventUpdate = {
        title: this.title.trim(),
        description: this.description?.trim() || null,
        start_time: startISO,
        end_time: endISO,
        all_day: this.allDay,
        event_type: this.eventType,
        location: this.location?.trim() || null,
        company_id: this.selectedCompanyId,
        target_user_id: this.targetUserId === -1 ? null : this.targetUserId,
      };
      this.dialogRef.close(update);
    } else {
      const create: CalendarEventCreate = {
        title: this.title.trim(),
        description: this.description?.trim() || null,
        start_time: startISO,
        end_time: endISO,
        all_day: this.allDay,
        event_type: this.eventType,
        location: this.location?.trim() || null,
        company_id: this.selectedCompanyId,
        target_user_id: this.targetUserId === -1 ? null : this.targetUserId,
      };
      this.dialogRef.close(create);
    }
  }

  private isValidDate(date: Date | null): boolean {
    return !!date && !isNaN(date.getTime());
  }

  private isValidTime(time: string): boolean {
    if (!time) return false;
    const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(time);
    return !!match;
  }

  /** Ellenőrzi, hogy az adott időpont munkaidőn (06:00–20:00) kívül esik-e */
  isTimeOutsideWorkHours(time: string): boolean {
    if (!this.isValidTime(time)) return false;
    const [h, m] = time.split(':').map(Number);
    const totalMinutes = h * 60 + m;
    return totalMinutes < 6 * 60 || totalMinutes > 20 * 60;
  }

  get isFormValid(): boolean {
    if (!this.title.trim() || !this.isValidDate(this.startDate)) return false;
    if (!this.allDay && !this.isValidTime(this.startTime)) return false;
    if (!this.allDay && this.endDate && !this.isValidTime(this.endTime)) return false;
    if (!this.allDay && this.isTimeOutsideWorkHours(this.startTime)) return false;
    if (!this.allDay && this.endDate && this.isTimeOutsideWorkHours(this.endTime)) return false;
    return true;
  }
}
